import React from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import Tabs, { Tab, TabList, TabPanel } from '@atlaskit/tabs';
import { token } from '@atlaskit/tokens';
import { HierarchyChart, type HierarchyData } from '@atlassian/reporting-lines/hierarchy-chart';
import { LinkList } from '@atlassian/smart-knowledge-cards/link-list';

import type { Collaborator, PersonProfileData, RecentWorkUrls } from '../../types';

import { ActionBar } from './ActionBar';
import { Collaborators } from './Collaborators';
import { Overview } from './Overview';
import { ProfileIdentity } from './ProfileIdentity';

const messages = defineMessages({
	tabRecentWork: {
		id: 'user-profile-preview.full.tab-recent-work',
		defaultMessage: 'Recent work',
		description: 'Tab label for the recent work section in the user profile preview',
	},
	tabReportingLine: {
		id: 'user-profile-preview.full.tab-reporting-line',
		defaultMessage: 'Reporting line',
		description: 'Tab label for the reporting line section in the user profile preview',
	},
	tabCollaborators: {
		id: 'user-profile-preview.full.tab-collaborators',
		defaultMessage: 'Collaborators',
		description: 'Tab label for collaborators on the user profile preview',
	},
	tabOverview: {
		id: 'user-profile-preview.full.tab-overview',
		defaultMessage: 'Overview',
		description: 'Tab label for the overview section in the user profile preview',
	},
});

const styles = cssMap({
	tabPanelSurface: {
		marginTop: token('space.150'),
		width: '100%',
		backgroundColor: token('elevation.surface'),
		borderRadius: token('radius.large'),
	},
	tabPanelInsetLoose: {
		paddingTop: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.300'),
		paddingRight: token('space.300'),
	},
	tabPanelInsetTight: {
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
	},
});

interface TenureData {
	beforePercent: number;
	beforeCount: number;
	afterCount: number;
}

interface FullVariantProps {
	data: PersonProfileData;
	hierarchyData?: HierarchyData;
	recentWorkData?: RecentWorkUrls;
	collaborators?: Collaborator[];
	tenureData?: TenureData;
	testId?: string;
	cloudId: string;
}

export function FullVariant({
	data,
	hierarchyData,
	recentWorkData,
	collaborators,
	tenureData,
	testId,
	cloudId,
}: FullVariantProps) {
	const intl = useIntl();
	const hasRecentWork = !!(recentWorkData && recentWorkData.length > 0);
	const hasHierarchy = !!hierarchyData && hierarchyData.user?.name;
	const hasCollaborators = !!collaborators && collaborators.length > 0;
	const showTabs = hasRecentWork || hasHierarchy || hasCollaborators;

	return (
		<Stack space="space.150" testId={testId}>
			<Inline space="space.200" alignBlock="center">
				<ProfileIdentity
					data={data}
					avatarSize="xlarge"
					showDepartment
					showLocation
					actions={<ActionBar data={data} showOrgChart={false} cloudId={cloudId} />}
				/>
			</Inline>

			{showTabs && (
				<Tabs id="person-profile-card-tabs">
					<TabList>
						{hasRecentWork && <Tab>{intl.formatMessage(messages.tabRecentWork)}</Tab>}
						{hasHierarchy && <Tab>{intl.formatMessage(messages.tabReportingLine)}</Tab>}
						{hasCollaborators && <Tab>{intl.formatMessage(messages.tabCollaborators)}</Tab>}
						<Tab>{intl.formatMessage(messages.tabOverview)}</Tab>
					</TabList>

					{hasRecentWork && (
						<TabPanel>
							<Box xcss={styles.tabPanelSurface}>
								<Box testId={testId ? `${testId}-recent-work` : undefined}>
									<LinkList urls={recentWorkData} />
								</Box>
							</Box>
						</TabPanel>
					)}
					{hasHierarchy && (
						<TabPanel>
							<Box xcss={cx(styles.tabPanelSurface, styles.tabPanelInsetLoose)}>
								{hierarchyData ? <HierarchyChart data={hierarchyData} /> : null}
							</Box>
						</TabPanel>
					)}
					{hasCollaborators && collaborators && (
						<TabPanel>
							<Box xcss={cx(styles.tabPanelSurface, styles.tabPanelInsetTight)}>
								<Collaborators collaborators={collaborators} cloudId={cloudId} />
							</Box>
						</TabPanel>
					)}
					<TabPanel>
						<Box xcss={cx(styles.tabPanelSurface, styles.tabPanelInsetLoose)}>
							<Overview data={data} tenureData={tenureData} />
						</Box>
					</TabPanel>
				</Tabs>
			)}
		</Stack>
	);
}
